import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useDispatch, useSelector } from 'react-redux';
import { addDoc, collection, serverTimestamp } from 'firebase/firestore';
import { db } from '../../../../../../utils/firebase/firebase.utils.js';
import { selectUser } from '../../../../../../store/user/user.selector';
import { setSystemNotification } from '../../../../../../store/system/system.action.js';
import {
  ModalOverlay,
  ModalContainer,
  ModalHeader,
  ModalTitle,
  CloseButton,
  ModalBody,
  ModalFooter,
  ButtonGroup,
  SaveButton,
  CancelButton,
  FormRow,
  FormGroup,
  ErrorMessage
} from './AddInventoryModal.styles.js';

// components
import InputBar from '../../../../../../components/input-bar/InputBar.js';

export default function AddInventoryModal({ isOpen, onClose, onSuccess }) {
  const dispatch = useDispatch();
  const user = useSelector(selectUser);
  const [isSaving, setIsSaving] = useState(false);
  const [errors, setErrors] = useState({});
  const [formData, setFormData] = useState({
    itemName: '',
    quantity: '',
    totalCost: '',
    url: ''
  });

  useEffect(() => {
    if (isOpen) {
      setFormData({
        itemName: '',
        quantity: '',
        totalCost: '',
        url: ''
      });
      setErrors({});
    }
  }, [isOpen]);

  const handleClose = () => {
    if (isSaving) return;
    onClose();
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.itemName.trim()) {
      newErrors.itemName = 'Item name is required';
    }

    const quantityValue = parseInt(formData.quantity, 10);
    if (!Number.isFinite(quantityValue) || quantityValue <= 0) {
      newErrors.quantity = 'Quantity must be a positive number';
    }

    const totalCostValue = parseFloat(formData.totalCost);
    if (!Number.isFinite(totalCostValue) || totalCostValue < 0) {
      newErrors.totalCost = 'Total cost must be a valid number';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    if (!user?.uid) {
      dispatch(setSystemNotification('You must be logged in to add inventory'));
      return;
    }

    setIsSaving(true);
    try {
      const inventoryRef = collection(db, `users/${user.uid}/inventory`);
      await addDoc(inventoryRef, {
        itemName: formData.itemName.trim(),
        quantity: parseInt(formData.quantity, 10),
        totalCost: parseFloat(formData.totalCost),
        url: formData.url.trim(),
        createdAt: serverTimestamp()
      });

      dispatch(setSystemNotification('Inventory item added'));
      onSuccess?.();
      onClose();
    } catch (error) {
      // Error adding inventory item
      dispatch(setSystemNotification('Error adding inventory item'));
    } finally {
      setIsSaving(false);
    }
  };

  if (!isOpen) return null;

  const modalContent = (
    <ModalOverlay onClick={handleClose}>
      <ModalContainer className='panel-bg' onClick={(e) => e.stopPropagation()}>
        <ModalHeader>
          <ModalTitle>Add Inventory</ModalTitle>
          <CloseButton onClick={handleClose}>×</CloseButton>
        </ModalHeader>
        <form onSubmit={handleSubmit}>
          <ModalBody>
            <FormGroup>
              <InputBar
                label="Item name"
                value={formData.itemName}
                updateValue={(value) => setFormData((prev) => ({ ...prev, itemName: value }))}
              />
              {errors.itemName && <ErrorMessage>{errors.itemName}</ErrorMessage>}
            </FormGroup>
            <FormRow>
              <FormGroup>
                <InputBar
                  label="Quantity"
                  type="number"
                  value={formData.quantity}
                  updateValue={(value) => setFormData((prev) => ({ ...prev, quantity: value }))}
                />
                {errors.quantity && <ErrorMessage>{errors.quantity}</ErrorMessage>}
              </FormGroup>
              <FormGroup>
                <InputBar
                  label="Total Cost"
                  type="number"
                  value={formData.totalCost}
                  updateValue={(value) => setFormData((prev) => ({ ...prev, totalCost: value }))}
                />
                {errors.totalCost && <ErrorMessage>{errors.totalCost}</ErrorMessage>}
              </FormGroup>
            </FormRow>
            <FormRow>
              <FormGroup>
                <InputBar
                  label="URL"
                  type="url"
                  value={formData.url}
                  updateValue={(value) => setFormData((prev) => ({ ...prev, url: value }))}
                />
              </FormGroup>
            </FormRow>
          </ModalBody>
          <ModalFooter>
            <ButtonGroup>
              <CancelButton type="button" onClick={handleClose} disabled={isSaving}>
                Cancel
              </CancelButton>
              <SaveButton type="submit" disabled={isSaving}>
                {isSaving ? 'Saving...' : 'Save'}
              </SaveButton>
            </ButtonGroup>
          </ModalFooter>
        </form>
      </ModalContainer>
    </ModalOverlay>
  );

  return createPortal(modalContent, document.body);
}
